import { useCallback, useMemo } from "react";
import { isUUID } from "@common/helpers/uuid";
import {
    createEnumParam,
    DelimitedArrayParam,
    StringParam,
    useQueryParams,
} from "use-query-params";
import {
    SchedulesReportLayout,
    SchedulesReportMethod,
    SchedulesReportRevenueSubMode,
    SchedulesReportRows,
} from "@common/types/reports/schedules-report";
import {
    isSchedulesReportDatePresetId,
    SCHEDULES_REPORT_DATE_PRESET_IDS,
    SchedulesReportDatePresetId,
} from "./schedulesReportDatePresets";
import { SchedulesReportParams } from "./schedulesReportTypes";

export const DEFAULT_SCHEDULES_REPORT_LAYOUT =
    SchedulesReportLayout.rollforward;
export const DEFAULT_SCHEDULES_REPORT_ROWS = SchedulesReportRows.bySchedule;
export const DEFAULT_SCHEDULES_REPORT_REVENUE_SUB_MODE =
    SchedulesReportRevenueSubMode.all;
export const DEFAULT_SCHEDULES_REPORT_METHOD = SchedulesReportMethod.all;

export type SchedulesReportPresentationParams = Required<
    Pick<SchedulesReportParams, "layout" | "rows" | "revenueSubMode" | "method">
>;

export function useSchedulesReportParams() {
    const [query, setQuery] = useQueryParams({
        layout: createEnumParam(Object.values(SchedulesReportLayout)),
        rows: createEnumParam(Object.values(SchedulesReportRows)),
        revenueSubMode: createEnumParam(
            Object.values(SchedulesReportRevenueSubMode),
        ),
        method: createEnumParam(Object.values(SchedulesReportMethod)),
        datePreset: createEnumParam([...SCHEDULES_REPORT_DATE_PRESET_IDS]),
        view: StringParam,
        scheduleIds: DelimitedArrayParam,
    });

    const params = useMemo(
        (): SchedulesReportPresentationParams => ({
            layout: query.layout ?? DEFAULT_SCHEDULES_REPORT_LAYOUT,
            rows: query.rows ?? DEFAULT_SCHEDULES_REPORT_ROWS,
            revenueSubMode:
                query.revenueSubMode ??
                DEFAULT_SCHEDULES_REPORT_REVENUE_SUB_MODE,
            method: query.method ?? DEFAULT_SCHEDULES_REPORT_METHOD,
        }),
        [query],
    );

    const datePreset = isSchedulesReportDatePresetId(query.datePreset)
        ? query.datePreset
        : null;

    const setParams = useCallback(
        (changes: Partial<SchedulesReportPresentationParams>) =>
            setQuery({ ...changes }, "replaceIn"),
        [setQuery],
    );

    const setDatePreset = useCallback(
        (preset: SchedulesReportDatePresetId | null) =>
            setQuery({ datePreset: preset ?? undefined }, "replaceIn"),
        [setQuery],
    );

    const scheduleApplicationIds = useMemo(
        () =>
            (query.scheduleIds ?? []).filter(
                (id): id is string => typeof id === "string" && isUUID(id),
            ),
        [query.scheduleIds],
    );

    const clearScheduleApplicationIds = useCallback(
        () => setQuery({ scheduleIds: undefined }, "replaceIn"),
        [setQuery],
    );

    const clearViewId = useCallback(
        () => setQuery({ view: undefined }, "replaceIn"),
        [setQuery],
    );

    return {
        params,
        datePreset,
        viewId: query.view ?? null,
        scheduleApplicationIds,
        clearScheduleApplicationIds,
        setParams,
        setDatePreset,
        clearViewId,
    };
}
